import { connect } from "react-redux";
import React, { Fragment, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import useToggle from "react-use-toggle";

import * as ROUTES from "../../constants/routes";
import * as actions from "../../store/actions";
import ContentWrapper from "../ContentWrapper";
import List from "../List";
import ListItem from "../ListItem";
import ManualNav from "../ManualNav";
import ManualSearchForm from "../ManualSearchForm";
import MenuBtn from "../MenuBtn";
import MobileHeader from "../MobileHeader";
import PageHeader from "../PageHeader";
import ScrollToTopHOC from "../ScrollToTopHOC/ScrollToTopHOC";
import SideMenu from "../SideMenu";
import {
	SEARCH_RESULT_LIMIT,
	buildSearchIndex,
	queryParam,
	searchIndex,
	snippetAround,
	stripHtml,
} from "../../util/searchManual";

import styles from "./ManualSearchResults.module.css";

// Stable identity so the index memo does not re-run while the pages snapshot is
// still loading. It has to live here rather than in mapStateToProps, where a
// `|| {}` would mint a fresh object on every store change and defeat the memo.
const EMPTY_PAGES = {};

const ManualSearchResults = (props) => {
	const { allPages, topLevelPages, setCurrentPage } = props;

	const location = useLocation();
	const [menuState, toggleMenu] = useToggle(false);

	const q = queryParam(location.search, "q");
	const displayQuery = q.trim();
	const pages = allPages || EMPTY_PAGES;

	const index = useMemo(() => buildSearchIndex(pages), [pages]);
	const results = useMemo(() => searchIndex(index, q), [index, q]);

	const normalized = displayQuery.toLowerCase();
	const terms = normalized ? normalized.split(/\s+/).filter(Boolean) : [];

	// Only fire close menu if it is open, matching ReadingPane: closeMenu is null
	// when the drawer is shut, which onMouseUp tolerates and a click handler does not.
	const closeMenu = menuState ? () => toggleMenu() : null;

	const renderResult = (record) => {
		const blockKey =
			record.blockIndex === null ? "page" : record.blockIndex;
		const to =
			record.blockIndex === null
				? ROUTES.MANUAL + "/" + record.slug
				: ROUTES.MANUAL +
				  "/" +
				  record.slug +
				  "?block=" +
				  record.blockIndex;

		const plainText = record.contentHTML
			? stripHtml(record.contentHTML)
			: "";
		const snippet = plainText
			? snippetAround(plainText, terms, normalized)
			: null;

		// A top level page is its own section, so the breadcrumb would repeat it.
		const breadcrumb =
			record.sectionTitle && record.sectionTitle !== record.title ? (
				<span className={styles.Breadcrumb}>
					{record.sectionTitle}{" "}
					<span className={styles.Chevron}>›</span> {record.title}
				</span>
			) : null;

		return (
			<ListItem
				className={styles.Result}
				key={record.id + "-" + blockKey}
			>
				<Link
					className={styles.ResultLink}
					to={to}
					onClick={() => setCurrentPage(record.slug)}
				>
					<span className={styles.ResultTitle}>{record.title}</span>
					{breadcrumb}
					{record.header ? (
						<span className={styles.BlockHeader}>
							{record.header}
						</span>
					) : null}
					{snippet ? (
						<span className={styles.Snippet}>
							{snippet.before}
							<mark className={styles.Mark}>{snippet.match}</mark>
							{snippet.after}
						</span>
					) : null}
				</Link>
			</ListItem>
		);
	};

	const rendered = results.slice(0, SEARCH_RESULT_LIMIT);

	let body;
	if (!displayQuery) {
		body = (
			<p className={styles.Hint}>Enter a term to search the manual.</p>
		);
	} else if (!results.length) {
		body = (
			<p className={styles.Hint}>
				No results for &quot;{displayQuery}&quot;.
			</p>
		);
	} else {
		body = (
			<Fragment>
				<h1 className={styles.Count}>
					{results.length} result{results.length === 1 ? "" : "s"} for
					&quot;{displayQuery}&quot;
				</h1>
				<List
					className={styles.ResultList}
					margin="0"
					padding="0"
					listStyle="none"
				>
					{rendered.map(renderResult)}
				</List>
				{results.length > rendered.length ? (
					<p className={styles.Truncated}>
						Showing the first {rendered.length} of {results.length}.
					</p>
				) : null}
			</Fragment>
		);
	}

	return (
		<ScrollToTopHOC>
			<ContentWrapper>
				<MobileHeader>
					Search the Manual{" "}
					<MenuBtn
						clickHandler={toggleMenu}
						anim="Squeeze"
						active={menuState}
					/>
				</MobileHeader>
				<div className={styles.ResultsPane}>
					<PageHeader title="Search Results">
						<div className={styles.HeaderSearch}>
							<ManualSearchForm
								variant="page"
								initialQuery={q}
								autoFocus={!displayQuery}
							/>
						</div>
					</PageHeader>
					<div className={styles.SearchFormContainer}>
						{body}
					</div>
					<SideMenu menuState={menuState} closeMenu={toggleMenu}>
						<ManualSearchForm
							variant="sidebar"
							initialQuery={q}
							onSubmitted={closeMenu}
						/>
						{topLevelPages ? (
							<ManualNav
								pagelist={topLevelPages}
								closeMenu={closeMenu}
								navLevel={0}
							/>
						) : null}
					</SideMenu>
				</div>
			</ContentWrapper>
		</ScrollToTopHOC>
	);
};

const mapStateToProps = ({ pages }) => {
	return {
		allPages: pages.pages,
		topLevelPages: pages.topLevelPages,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		setCurrentPage: (slug) => dispatch(actions.setCurrentPage(slug)),
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(ManualSearchResults);
